import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchRefollowCompletedByDate,
  fetchRefollowCompletedDates,
  formatDomainCategory,
  formatMemberAddress,
  formatStatsDisplayDate,
} from '../../../utils/marketingApi';
import RefollowSourceBatchChip from './RefollowSourceBatchChip';
import cm from '../CallManager.module.scss';
import s from './Refollow.module.scss';

const DATES_PAGE_SIZE = 5;
const ROWS_PAGE_SIZE = 8;

function matchesSearch(row, query) {
  const t = (query || '').trim().toLowerCase();
  if (!t || t.length < 2) return true;
  const digits = t.replace(/\D/g, '');
  if (digits.length >= 4) {
    const ph = String(row.phone_e164 || '').replace(/\D/g, '');
    return ph.includes(digits);
  }
  const hay = [
    row.name,
    row.phone_e164,
    String(row.customer_id),
    row.address,
    row.domain,
    row.category,
  ].join(' ').toLowerCase();
  return hay.includes(t);
}

function completedRowClass(row) {
  const st = String(row.status || '').toLowerCase();
  if (st === 'done') return cm.statsRowGood;
  if (st === 'cancelled') return cm.statsRowDrop;
  return cm.statsRowDefault;
}

function outcomeLabel(row) {
  const st = String(row.status || '').toLowerCase();
  if (st === 'done') return 'Done';
  if (st === 'cancelled') return 'Cancelled';
  return row.status || '—';
}

function customerLine(row) {
  const id = row.customer_id;
  const name = (row.name || '').trim() || '—';
  if (id) return `#${id} ${name}`;
  return name;
}

function waCell(row) {
  if (row.wa_display) return row.wa_display;
  const wa = row.whatsapp;
  if (wa?.status_label) return wa.status_label;
  if (!wa?.sent && !wa?.send_status) return 'Not sent';
  return wa.send_status || '—';
}

export default function RefollowCompletedPanel({
  open,
  onClose,
  showToast,
  onOpenCustomer,
  refreshTick = 0,
  searchQuery = '',
}) {
  const [datesPage, setDatesPage] = useState(1);
  const [datesMeta, setDatesMeta] = useState({ total: 0, total_pages: 1, items: [] });
  const [datesLoading, setDatesLoading] = useState(false);
  const [viewDate, setViewDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowsPage, setRowsPage] = useState(1);
  const [rowsMeta, setRowsMeta] = useState({ total: 0, total_pages: 1, page: 1 });
  const [sourceBatches, setSourceBatches] = useState([]);
  const [daySummary, setDaySummary] = useState({ total: 0, done_count: 0, cancelled_count: 0 });
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [sourceBatchFilter, setSourceBatchFilter] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const rowsLoadGen = useRef(0);

  const resetDrill = useCallback(() => {
    setViewDate(null);
    setRows([]);
    setRowsPage(1);
    setRowsMeta({ total: 0, total_pages: 1, page: 1 });
    setSourceBatches([]);
    setDaySummary({ total: 0, done_count: 0, cancelled_count: 0 });
    setOutcomeFilter('all');
    setSourceBatchFilter(null);
  }, []);

  const handleClose = useCallback(() => {
    resetDrill();
    setDatesPage(1);
    onClose?.();
  }, [onClose, resetDrill]);

  const loadDates = useCallback(async (page) => {
    setDatesLoading(true);
    try {
      const data = await fetchRefollowCompletedDates({ page, pageSize: DATES_PAGE_SIZE });
      setDatesMeta({
        total: data?.total || 0,
        total_pages: data?.total_pages || 1,
        items: data?.items || [],
      });
    } catch (e) {
      showToast?.(e.message || 'Could not load completed dates', 'error');
      setDatesMeta({ total: 0, total_pages: 1, items: [] });
    } finally {
      setDatesLoading(false);
    }
  }, [showToast]);

  const loadRows = useCallback(async (date, page, status, batchId) => {
    if (!date) {
      setRows([]);
      return;
    }
    const gen = rowsLoadGen.current + 1;
    rowsLoadGen.current = gen;
    setRowsLoading(true);
    try {
      const data = await fetchRefollowCompletedByDate(date, {
        page,
        pageSize: ROWS_PAGE_SIZE,
        status: status || 'all',
        sourceBatchId: batchId,
      });
      if (gen !== rowsLoadGen.current) return;
      setRows(data?.items || []);
      setSourceBatches(data?.source_batches || []);
      setDaySummary(data?.summary || { total: 0, done_count: 0, cancelled_count: 0 });
      setRowsMeta({
        total: data?.total || 0,
        total_pages: data?.total_pages || 1,
        page: data?.page || page,
      });
    } catch (e) {
      if (gen !== rowsLoadGen.current) return;
      showToast?.(e.message || 'Could not load completed follow-ups', 'error');
      setRows([]);
    } finally {
      if (gen === rowsLoadGen.current) setRowsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!open) return;
    loadDates(datesPage);
  }, [open, datesPage, loadDates, refreshTick]);

  useEffect(() => {
    if (!open || !viewDate) return;
    loadRows(viewDate, rowsPage, outcomeFilter, sourceBatchFilter);
  }, [open, viewDate, rowsPage, outcomeFilter, sourceBatchFilter, loadRows, refreshTick]);

  const setOutcomeFilterAndReset = (next) => {
    setOutcomeFilter(next);
    setRowsPage(1);
  };

  const toggleCompletedBatchFilter = (batchId) => {
    setSourceBatchFilter((prev) => (prev === batchId ? null : batchId));
    setRowsPage(1);
  };

  const visibleRows = useMemo(
    () => rows.filter((row) => matchesSearch(row, searchQuery)),
    [rows, searchQuery],
  );

  if (!open) return null;

  const dateLabel = viewDate ? formatStatsDisplayDate(viewDate) : '';
  const rowsTotalPages = rowsMeta.total_pages || 1;
  const showRowsPager = rowsMeta.total > 0 && rowsTotalPages > 1;
  const pageStart = rowsMeta.total
    ? (rowsPage - 1) * ROWS_PAGE_SIZE + 1
    : 0;
  const pageEnd = Math.min(rowsPage * ROWS_PAGE_SIZE, rowsMeta.total);

  return (
    <div className={cm.modalBackdrop} onClick={handleClose}>
      <div
        className={`${cm.completedModal}${viewDate ? ` ${cm.completedModalWide} ${s.refollowCompletedModalWide}` : ''}`}
        role="dialog"
        aria-label="Completed follow-ups"
        onClick={(e) => e.stopPropagation()}
      >
        {!viewDate ? (
          <>
            <div className={cm.completedModalHead}>
              <div>
                <h2>Completed follow-ups</h2>
              </div>
              <div className={cm.completedModalActions}>
                <button type="button" className={cm.completedClose} onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
            {datesLoading && !datesMeta.items.length ? (
              <div className={cm.loading}>Loading dates…</div>
            ) : null}
            {!datesLoading && datesMeta.items.length === 0 ? (
              <div className={cm.empty}>No completed follow-ups yet.</div>
            ) : (
              <ul className={cm.completedList}>
                {datesMeta.items.map(({ date, done_count, cancelled_count }) => (
                  <li key={date}>
                    <button
                      type="button"
                      className={cm.completedItem}
                      onClick={() => {
                        setViewDate(date);
                        setRowsPage(1);
                        setRows([]);
                        setOutcomeFilter('all');
                        setSourceBatchFilter(null);
                      }}
                    >
                      <div className={cm.completedItemInner}>
                        <div className={cm.completedItemMain}>
                          <div className={cm.completedItemTitle}>
                            <span className={cm.batchName}>
                              {formatStatsDisplayDate(date)}
                            </span>
                          </div>
                          <div className={s.refollowCompletedDateCounts}>
                            <span className={s.refollowCompletedCountDone}>
                              {done_count ?? 0}
                              {' done'}
                            </span>
                            <span className={s.refollowCompletedCountSep}>·</span>
                            <span className={s.refollowCompletedCountCancelled}>
                              {cancelled_count ?? 0}
                              {' cancelled'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {datesMeta.total > 0 ? (
              <div className={`${cm.completedPager} ${s.refollowCompletedPager}`}>
                {datesMeta.total_pages > 1 && datesPage > 1 ? (
                  <button
                    type="button"
                    className={cm.pagerBtn}
                    disabled={datesLoading}
                    onClick={() => setDatesPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                ) : null}
                <span className={s.refollowCompletedPagerMeta}>
                  {datesMeta.total_pages > 1
                    ? `Page ${datesPage} of ${datesMeta.total_pages} · ${datesMeta.total} days`
                    : `${datesMeta.total} day${datesMeta.total === 1 ? '' : 's'}`}
                </span>
                {datesMeta.total_pages > 1 && datesPage < datesMeta.total_pages ? (
                  <button
                    type="button"
                    className={cm.pagerBtn}
                    disabled={datesLoading}
                    onClick={() => setDatesPage((p) => p + 1)}
                  >
                    Next
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <section className={`${cm.statsFilterPanel} ${s.refollowCompletedDetail}`} aria-live="polite">
            <div className={s.refollowCompletedDetailHead}>
              <div className={s.refollowCompletedDetailMain}>
                <h3 className={s.refollowCompletedDetailDate}>{dateLabel}</h3>
                <div className={s.refollowCompletedOutcomeFilters} role="group" aria-label="Outcome filters">
                  <button
                    type="button"
                    className={`${s.refollowOutcomeFilterBtn}${outcomeFilter === 'all' ? ` ${s.refollowOutcomeFilterBtnActive}` : ''}`}
                    onClick={() => setOutcomeFilterAndReset('all')}
                  >
                    Total
                    <span className={s.refollowOutcomeFilterCount}>{daySummary.total ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    className={`${s.refollowOutcomeFilterBtn}${outcomeFilter === 'done' ? ` ${s.refollowOutcomeFilterBtnActive}` : ''}`}
                    onClick={() => setOutcomeFilterAndReset('done')}
                  >
                    Done
                    <span className={s.refollowOutcomeFilterCount}>{daySummary.done_count ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    className={`${s.refollowOutcomeFilterBtn}${outcomeFilter === 'cancelled' ? ` ${s.refollowOutcomeFilterBtnActive}` : ''}`}
                    onClick={() => setOutcomeFilterAndReset('cancelled')}
                  >
                    Cancelled
                    <span className={s.refollowOutcomeFilterCount}>{daySummary.cancelled_count ?? 0}</span>
                  </button>
                </div>
                {sourceBatches.length > 0 ? (
                  <div className={s.followUpSourceChipRow} aria-label="Filter by CM batch">
                    {sourceBatches.map((b) => (
                      <RefollowSourceBatchChip
                        key={b.id}
                        opsBatchId={b.id}
                        name={b.name}
                        filterActive={sourceBatchFilter === b.id}
                        onFilterToggle={toggleCompletedBatchFilter}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
              <div className={s.refollowCompletedDetailActions}>
                <button type="button" className={cm.statsFilterClose} onClick={resetDrill}>
                  Back to dates
                </button>
                <button type="button" className={s.refollowCompletedModalClose} onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
            <div className={`${s.refollowCompletedTableShell}${rowsLoading ? ` ${s.refollowCompletedTableLoading}` : ''}`}>
              <div className={cm.statsFilterTableWrap}>
                <table className={cm.statsFilterTable}>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>CM batch</th>
                      <th>Address</th>
                      <th>Domain · category</th>
                      <th>WhatsApp</th>
                      <th>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 && !rowsLoading ? (
                      <tr>
                        <td colSpan={7} className={cm.statsFilterEmpty}>
                          {rows.length && (searchQuery || '').trim().length >= 2
                            ? 'No matches for your search.'
                            : 'Nothing completed on this day.'}
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => {
                        const batchId = row.source_batch_id;
                        const batchName = (row.source_batch_name || '').trim();
                        return (
                          <tr key={row.id} className={completedRowClass(row)}>
                            <td>
                              <button
                                type="button"
                                className={`${cm.statsFilterBatchLink} ${s.refollowCompletedCustomerLink}`}
                                onClick={() => onOpenCustomer?.(row)}
                              >
                                {customerLine(row)}
                              </button>
                            </td>
                            <td>{row.phone_e164 || '—'}</td>
                            <td>
                              {batchId ? (
                                <RefollowSourceBatchChip
                                  opsBatchId={batchId}
                                  name={batchName}
                                  filterActive={sourceBatchFilter === batchId}
                                  onFilterToggle={toggleCompletedBatchFilter}
                                />
                              ) : '—'}
                            </td>
                            <td>{formatMemberAddress(row)}</td>
                            <td>{formatDomainCategory(row) || '—'}</td>
                            <td>{waCell(row)}</td>
                            <td>{outcomeLabel(row)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {rowsMeta.total > 0 ? (
              <div className={`${cm.statsFilterPager} ${s.refollowCompletedPager}`}>
                {showRowsPager && rowsPage > 1 ? (
                  <button
                    type="button"
                    disabled={rowsLoading}
                    onClick={() => setRowsPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                ) : null}
                <span className={s.refollowCompletedPagerMeta}>
                  {showRowsPager
                    ? `${pageStart}–${pageEnd} of ${rowsMeta.total} · Page ${rowsPage} of ${rowsTotalPages}`
                    : `${rowsMeta.total} customer${rowsMeta.total === 1 ? '' : 's'}`}
                </span>
                {showRowsPager && rowsPage < rowsTotalPages ? (
                  <button
                    type="button"
                    disabled={rowsLoading}
                    onClick={() => setRowsPage((p) => p + 1)}
                  >
                    Next
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>
        )}
      </div>
    </div>
  );
}
